/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/features/attendanceModern/screens/HistoryContent.tsx
 * PURPOSE: SC-017-EMP-ATTENDANCE [11] history — nội dung dùng CHUNG giữa route
 *          độc lập `/attendance/history` (frontend/src/pages/AttendanceHistory.tsx,
 *          vẫn giữ nguyên làm lối vào trực tiếp) và screenState 'history' mới
 *          trong luồng chấm công (AttendanceModern.tsx, đến từ [1]/[5]/[10]).
 *          Trích nguyên vẹn từ AttendanceHistory.tsx — KHÔNG đổi hành vi, chỉ
 *          tách phần thân ra khỏi khung trang (header/EmployeeBottomNav) để
 *          dùng lại được ở 2 nơi mà không nhân đôi 2 lớp chrome.
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-02
 * COMPLIANCE: SDLC 6.5.0 + WIREFRAME-V0-MASTER-REGISTRY.md §SC-017-EMP-ATTENDANCE [11]
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 2: Milestone-Gated Roadmaps
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/persona-flows/WIREFRAME-V0-MASTER-REGISTRY.md §SC-017-EMP-ATTENDANCE
 * - Origin: frontend/src/pages/AttendanceHistory.tsx (giữ nguyên làm route độc lập)
 * - Đối chiếu mobile: mobile/src/screens/attendance/AttendanceScreen.tsx renderHistory()
 *
 * 2026-09-04 — người dùng: "màn lịch sử chấm công đang có layout useless".
 * Đo trên dữ liệu thật (480px): thẻ "Tăng ca" chiếm 244px ĐẦU màn với 4 ô + 3
 * dòng, phần lớn là số 0 với nhân viên không tăng ca; danh sách chấm công —
 * thứ người ta mở màn này để xem — bị đẩy xuống dưới nếp gấp. Bốn khiếm
 * khuyết thật, đã vá trong vòng này:
 *   (1) Ưu tiên hiển thị ngược + thẻ OT phình. Nay theo đúng mobile: 2 ô số to
 *       (đã dùng / còn lại) + 2 dòng chữ nhỏ, ~130px thay vì 244px.
 *   (2) Tiêu đề in mã máy "Tăng ca tháng 2026-09" → "tháng 9/2026" (cùng họ
 *       lỗi rò enum thô ra UI mà BS-301 AC #2 đã cấm ở màn Lịch).
 *   (3) KHÔNG nhãn khoảng thời gian: hook cứng 7 ngày gần nhất, người dùng
 *       không biết đang xem khoảng nào và không đổi được — trong khi thẻ OT
 *       lại nói về THÁNG. Hai khối, hai khoảng thời gian, không nhãn nào phân
 *       biệt. Nay có nhãn khoảng + chọn 7/30 ngày + dòng tổng hợp của chính
 *       khoảng đang xem.
 *   (4) Dòng bản ghi thiếu SỐ GIỜ LÀM và trạng thái duyệt khi chưa duyệt —
 *       backend đã trả `total_hours` (curl kiểm chứng), mobile đã hiện, chỉ
 *       web bỏ sót. Thứ tự nhãn theo mobile: quyết định của quản lý ưu tiên
 *       hơn cờ trễ/sớm thô.
 *  */

import React, { useMemo, useState } from 'react';
import { useSelfAttendanceHistoryRange, useSelfOtSummaryRange } from '@/hooks/useManagerProfile';
import {
  ContinuousScrollDatePicker,
  type DateFilterValue,
} from '@/components/common/ContinuousScrollDatePicker';
import type { SelfAttendanceRecord } from '@/services/api/managerProfileApi';
import { AppError } from '@/types/errors';
import styles from '@/pages/AttendanceHistory.module.css';

const VN_OFFSET_MS = 7 * 60 * 60 * 1000;
const WEEKDAY_VI = ['CN', 'Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7'];
const pad2 = (n: number) => String(n).padStart(2, '0');

const vnWallClock = (iso: string) => {
  const vn = new Date(new Date(iso).getTime() + VN_OFFSET_MS);
  return { mo: vn.getUTCMonth(), d: vn.getUTCDate(), h: vn.getUTCHours(), m: vn.getUTCMinutes(), dow: vn.getUTCDay() };
};

export const fmtDate = (iso: string) => {
  const { mo, d, dow } = vnWallClock(iso);
  return `${WEEKDAY_VI[dow]}, ${pad2(d)}/${pad2(mo + 1)}`;
};

export const fmtTime = (iso: string) => {
  const { h, m } = vnWallClock(iso);
  return `${pad2(h)}:${pad2(m)}`;
};

/**
 * Số giờ (thập phân từ backend `total_hours`) → chuỗi người đọc được.
 * 0.14 → "8 phút" · 8 → "8h" · 8.5 → "8h30". Trả null khi không có số để
 * hiển thị (bản ghi chưa chấm ra) — gọi phía trên quyết định ẩn, KHÔNG in "0h"
 * vì "0h" đọc như đã làm 0 giờ, khác hẳn "chưa chấm ra".
 */
export function formatWorkHours(hours: number | null | undefined): string | null {
  if (hours == null || !Number.isFinite(hours) || hours <= 0) return null;
  const mins = Math.round(hours * 60);
  if (mins < 60) return `${mins} phút`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h${pad2(m)}`;
}

/** Ngày local dạng "YYYY-MM-DD" (không đi qua UTC — xem fmtDate ở trên). */
const toLocalISO = (d: Date) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/** "2026-08-29" → "29/08" để in nhãn khoảng ngày. */
const shortVN = (iso: string) => {
  const [, mo, d] = iso.split('-');
  return `${d}/${mo}`;
};

/**
 * Liệt kê CHỈ những loại tăng ca thật sự có giờ. Bản cũ in cả 4 loại kể cả
 * khi bằng 0 ("Ngày thường 0h · Cuối tuần 0h · Ngày lễ 0h · Ca đêm 0h") —
 * bốn con số 0 không nói gì ngoài việc chiếm chỗ.
 */
export function otBreakdownParts(ot: {
  weekday_ot_hours: number; weekend_ot_hours: number;
  holiday_ot_hours: number; night_ot_hours: number;
}): string[] {
  return [
    ['Ngày thường', ot.weekday_ot_hours],
    ['Cuối tuần', ot.weekend_ot_hours],
    ['Ngày lễ', ot.holiday_ot_hours],
    ['Ca đêm', ot.night_ot_hours],
  ]
    .filter(([, h]) => Number(h) > 0)
    .map(([label, h]) => `${label} ${h}h`);
}


const renderResolutionLog = (record: SelfAttendanceRecord) => {
  if (record.resolution_source === 'manager') {
    const who = record.manager_resolved_by_name || 'Quản lý';
    const when = record.manager_resolved_at ? ` · ${fmtDate(record.manager_resolved_at)} ${fmtTime(record.manager_resolved_at)}` : '';
    return (
      <div className={styles.resolutionLine} data-testid={`resolution-log-${record.id}`}>
        Quản lý xử lý bởi {who}{when}
      </div>
    );
  }
  if (record.resolution_source === 'auto') {
    return (
      <div className={styles.resolutionLine} data-testid={`resolution-log-${record.id}`}>
        Tự động ghi nhận
      </div>
    );
  }
  return null;
};

const renderRecord = (record: SelfAttendanceRecord) => {
  const shiftLabel = record.shift_code || record.shift_name || null;
  const worked = formatWorkHours(record.total_hours);
  return (
    <li key={record.id} className={styles.recordRow} data-testid={`history-record-${record.id}`}>
      <div className={styles.recordDate}>{fmtDate(record.clock_in)}</div>
      <div className={styles.recordBody}>
        {shiftLabel && <div className={styles.recordShift}>{shiftLabel}</div>}
        <div className={styles.recordTime}>
          {fmtTime(record.clock_in)} – {record.clock_out ? fmtTime(record.clock_out) : '—:—'}
          {/* Số giờ làm: backend luôn trả, mobile luôn hiện, web trước đây bỏ
              sót — đây là con số người ta mở màn lịch sử để đối chiếu. */}
          {worked && <span className={styles.recordHours} data-testid={`record-hours-${record.id}`}> · {worked}</span>}
        </div>
        {renderResolutionLog(record)}
      </div>
      <div className={styles.recordBadges}>
        {/* Thứ tự theo mobile (AttendanceScreen.tsx renderHistory): quyết định
            của quản lý là kết luận CUỐI nên ưu tiên hơn cờ trễ/sớm thô. Bản
            web trước đây chỉ có "Đúng giờ"/"Trễ" + "Đã duyệt", nên bản ghi
            CHƯA duyệt không mang thông tin gì về việc còn phải chờ ai. */}
        {record.clock_out == null ? (
          <span className={`${styles.badge} ${styles.badgeMuted}`}>Chưa chấm ra</span>
        ) : record.is_approved ? (
          <span className={`${styles.badge} ${styles.badgeApproved}`}>Đã duyệt</span>
        ) : record.is_late ? (
          <span className={`${styles.badge} ${styles.badgeLate}`}>Đi muộn {record.late_minutes} phút · Chờ duyệt</span>
        ) : record.is_early_leave ? (
          <span className={`${styles.badge} ${styles.badgeLate}`}>Về sớm {record.early_leave_minutes} phút · Chờ duyệt</span>
        ) : (
          <span className={`${styles.badge} ${styles.badgeOk}`}>Chờ duyệt</span>
        )}
        {record.has_complaint && (
          <span className={`${styles.badge} ${styles.badgeComplaint}`} data-testid={`complaint-badge-${record.id}`}>
            Có khiếu nại
          </span>
        )}
      </div>
    </li>
  );
};

/**
 * Tổng hợp CHÍNH khoảng đang xem (khác thẻ OT — thẻ đó nói về cả THÁNG).
 * Chỉ đếm bản ghi đã chấm ra: bản ghi đang mở chưa có số giờ để cộng.
 */
export function summarizeWindow(records: SelfAttendanceRecord[]) {
  const closed = records.filter(r => r.clock_out != null);
  const days = new Set(closed.map(r => fmtDate(r.clock_in))).size;
  const hours = closed.reduce((sum, r) => sum + (Number.isFinite(r.total_hours) ? r.total_hours : 0), 0);
  const lateCount = records.filter(r => r.is_late).length;
  return { days, hours, lateCount, openCount: records.length - closed.length };
}

interface HistoryContentProps {
  /** Route độc lập: navigate('/attendance'). Nhúng trong flow: setScreenState('home'). */
  onClockInCta: () => void;
}

export const HistoryContent: React.FC<HistoryContentProps> = ({ onClockInCta }) => {
  // Mặc định 7 ngày gần nhất — giữ nguyên khoảng mà màn này vẫn hiển thị từ
  // trước, chỉ khác là nay NÓI RA khoảng đó và cho đổi.
  const [dateFilter, setDateFilter] = useState<DateFilterValue>(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 6);
    return { mode: 'range', from: toLocalISO(start), to: toLocalISO(end) };
  });

  const { startIso, endIso } = useMemo(
    () =>
      dateFilter.mode === 'single'
        ? { startIso: dateFilter.date, endIso: dateFilter.date }
        : { startIso: dateFilter.from, endIso: dateFilter.to },
    [dateFilter]
  );

  const historyQuery = useSelfAttendanceHistoryRange(startIso, endIso);
  // Thống kê hỏi theo ĐÚNG khoảng đang xem (không phải theo tháng cố định như
  // trước) — endpoint đã có sẵn nhánh khoảng ngày.
  const otQuery = useSelfOtSummaryRange(startIso, endIso);
  // bot-068 (GH#881): nhân viên mới tạo (chưa có bản ghi chấm công) từng thấy
  // banner lỗi chặn màn "Không tải được lịch sử" thay vì danh sách rỗng thân
  // thiện. 404 từ endpoint tự-xem lịch sử (self-view) nghĩa là "không có gì
  // để xem" chứ không phải hệ thống hỏng — chỉ lỗi mạng/5xx mới đáng hiện
  // banner chặn kèm nút "Thử lại".
  const historyError = historyQuery.error;
  const isHistoryNotFound = historyError instanceof AppError && historyError.statusCode === 404;
  const isHistoryHardError = historyQuery.isError && !isHistoryNotFound;
  const records = historyQuery.data?.records ?? [];
  const ot = otQuery.data;
  const summary = useMemo(() => summarizeWindow(records), [records]);
  const workedLabel = formatWorkHours(summary.hours);
  const otParts = ot ? otBreakdownParts(ot) : [];
  const hasViolations = !!ot && (ot.late_count > 0 || ot.early_count > 0 || ot.rest_violations > 0);

  return (
    <>
      {/* Thanh chọn khoảng — dùng ĐÚNG lịch của SC-009/SC-011
          (components/common/ContinuousScrollDatePicker.tsx: cuộn dọc liên tục,
          bấm-hai-lần chọn khoảng, nhảy tháng/năm/thập kỷ) thay vì tự dựng bộ
          chọn riêng cho màn này. */}
      <div className={styles.rangeBar}>
        <ContinuousScrollDatePicker
          value={dateFilter}
          onApply={setDateFilter}
          triggerClassName={styles.rangeTrigger}
          ariaLabel="Chọn ngày hoặc khoảng ngày xem lịch sử chấm công"
        />
        <button
          type="button"
          className={styles.rangeTodayButton}
          data-testid="history-range-today"
          onClick={() => {
            const end = new Date();
            const start = new Date();
            start.setDate(start.getDate() - 6);
            setDateFilter({ mode: 'range', from: toLocalISO(start), to: toLocalISO(end) });
          }}
        >
          7 ngày gần nhất
        </button>
      </div>

      {/* Thống kê CỦA KHOẢNG ĐANG XEM.
          Trước đây khối này là thẻ "Tăng ca tháng {YYYY-MM}" nói về một khoảng
          KHÁC (cả tháng) so với danh sách bên dưới (7 ngày), và trưng "trần
          40h / còn lại 40h" — con số đó là hằng số ghi cứng trong
          backend/app/services/ot_calculation_service.py (MONTHLY_OT_CAP_HOURS
          = 40, dùng ở cả nhánh khoảng lẫn nhánh tháng), KHÔNG đọc từ cấu hình
          `ot_monthly_cap_hours` của tenant (tenant_admin/settings.py) — nên nó
          là con số trông-như-luật nhưng không phải trần thật của tổ chức. Đã
          bỏ khỏi UI; chỉ hiển thị đại lượng ĐO ĐƯỢC từ bản ghi của chính nhân
          viên. Trần OT thật cần backend đọc cấu hình tenant — ghi nhận là việc
          riêng, không bịa số ở lớp hiển thị. */}
      <div className={styles.card} data-testid="history-stats-card">
        <div className={styles.statGrid}>
          <div className={styles.statTile}>
            <span className={styles.statValue} data-testid="stat-workdays">{summary.days}</span>
            <span className={styles.statLabel}>ngày công</span>
          </div>
          <div className={styles.statTile}>
            <span className={styles.statValue} data-testid="stat-hours">{workedLabel ?? '—'}</span>
            <span className={styles.statLabel}>giờ công</span>
          </div>
          {!!ot && ot.total_ot_hours > 0 && (
            <div className={styles.statTile}>
              <span className={styles.statValue} data-testid="stat-ot">{ot.total_ot_hours}h</span>
              <span className={styles.statLabel}>tăng ca</span>
            </div>
          )}
        </div>

        {otParts.length > 0 && (
          <p className={styles.otLine} data-testid="ot-breakdown">
            Tăng ca: {otParts.join(' · ')}
          </p>
        )}
        {hasViolations && ot && (
          <p className={styles.otLine} data-testid="ot-violation-counts">
            Đi muộn: {ot.late_count} lần ({ot.late_total_minutes} phút) · Về sớm: {ot.early_count} lần · Vi phạm nghỉ giữa ca: {ot.rest_violations} lần
          </p>
        )}
        {summary.openCount > 0 && (
          <p className={styles.otLine} data-testid="stat-open-count">
            {summary.openCount} bản ghi chưa chấm ra
          </p>
        )}
        {otQuery.isError && (
          <p className={styles.otLine} data-testid="ot-summary-error">
            Không tải được số liệu tăng ca của khoảng này.
          </p>
        )}
      </div>

      {historyQuery.isLoading && (
        <div role="status" aria-live="polite" className={styles.card}>
          <h2 className={styles.cardTitle}>Đang tải lịch sử...</h2>
          <p className={styles.cardDescription}>Vui lòng chờ trong giây lát.</p>
        </div>
      )}

      {!historyQuery.isLoading && isHistoryHardError && (
        <div className={styles.card}>
          <h2 className={styles.cardTitle}>Không tải được lịch sử</h2>
          <p className={styles.cardDescription}>Đã xảy ra lỗi khi tải dữ liệu chấm công.</p>
          <button onClick={() => historyQuery.refetch()} className={styles.retryButton}>
            Thử lại
          </button>
        </div>
      )}

      {!historyQuery.isLoading && !isHistoryHardError && records.length === 0 && (
        <div className={styles.card}>
          <h2 className={styles.cardTitle}>Chưa có dữ liệu chấm công</h2>
          <p className={styles.cardDescription}>
            Khoảng {shortVN(startIso)} – {shortVN(endIso)} bạn chưa có lần chấm công nào.
          </p>
          <button onClick={onClockInCta} className={styles.retryButton} data-testid="history-clock-in-cta">
            Chấm công ngay
          </button>
        </div>
      )}

      {!historyQuery.isLoading && !isHistoryHardError && records.length > 0 && (
        <ul className={styles.recordList} data-testid="attendance-history-list">
          {records.map(renderRecord)}
        </ul>
      )}
    </>
  );
};

export default HistoryContent;
